/* eslint-disable no-negated-condition */
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import get from 'lodash/get';
import filter from 'lodash/filter';
import head from 'lodash/head';
import pickBy from 'lodash/pickBy';
import isEmpty from 'lodash/isEmpty';
import { RUNNERS_ENTITY } from './reducer';

export const selectFields = (state) => get(state, 'agents.table.list.fields', []);

export const selectIsFetching = (state) => get(state, 'agents.isFetching', false);

export const selectUserAgents = (state) => get(state, 'agents.userAgents', []);

export const selectCloudAgents = (state) => get(state, 'agents.cloudAgents', []);

export const selectTriggerAgent = (state) => get(state, 'agents.triggerAgent', {});

export const selectTriggerAgentTools = (state) => get(state, 'agents.triggerAgent.executors', []);

export const selectTriggerAgentId = (state) => get(state, 'agents.triggerAgent.id', {});

export const selectTriggerAgentWs = (state, executorId) => {
  const executors = selectTriggerAgentTools(state);
  const executor = executors.find((tool) => tool.id === executorId);
  return executor?.parameters_data.workspaces_names || [];
};

export const selectTriggerMinSeverity = (state, executorId) => {
  const executors = selectTriggerAgentTools(state);
  const executor = executors.find((tool) => tool.id === executorId);
  const min = get(executor, 'parameters_data.min_severity', 'UNCLASSIFIED');
  return min ? min.toUpperCase() : 'UNCLASSIFIED';
};

export const selectTriggerMaxSeverity = (state, executorId) => {
  const executors = selectTriggerAgentTools(state);
  const executor = executors.find((tool) => tool.id === executorId);
  const max = get(executor, 'parameters_data.max_severity', 'CRITICAL');
  return max ? max.toUpperCase() : 'CRITICAL';
};

export const selectTriggerResolveHostname = (state, executorId) => {
  const executors = selectTriggerAgentTools(state);
  const executor = executors.find((tool) => tool.id === executorId);
  return executor?.parameters_data.resolve_hostname;
};

export const selectAgentTypes = (state) => get(state, 'agents.agentTypes', {});

export const selectToolsByCategory = state => {
  const tools = selectAgentTypes(state);
  const groupedTools = groupBy(tools, 'category');
  const mappedTools = map(groupedTools, (value, key) => ({ categoryName: key, items: value }));
  return mappedTools;
};

export const selectDraftNewAgentName = (state) => get(state, 'agents.draftNewAgent.name', '');
export const selectDraftNewAgentServer = (state) => get(state, 'agents.draftNewAgent.server', '');
export const selectDraftNewAgentTools = (state) => get(state, 'agents.draftNewAgent.tools', []);
export const selectDraftNewAgentDescription = (state) => get(state, 'agents.draftNewAgent.description', '');

export const selectToolsSelected = (state) => {
  const draftNewAgentTools = selectDraftNewAgentTools(state);
  const toolsSelected = draftNewAgentTools.map((e) => e.name);
  const agentTypes = selectAgentTypes(state);
  const filteredAgentTypes = Object.fromEntries(Object.entries(agentTypes).filter(([key, value]) => toolsSelected.indexOf(key) > -1));
  return filteredAgentTypes;
};

export const selectToolIsAdded = (state, tool) => {
  const draftNewAgentTools = selectDraftNewAgentTools(state);
  const toolsSelected = draftNewAgentTools.map((e) => e.name);
  return toolsSelected.indexOf(tool) > -1;
};

export const selectToolHasEnvVars = (state, tool) => {
  const allTools = selectAgentTypes(state);
  const toolSelected = pickBy(allTools, { name: tool });
  return toolSelected ? toolSelected[tool].environment_variables.length > 0 : false;
};

export const selectToolHasOptionalEnvVars = (state, tool) => {
  const allTools = selectAgentTypes(state);
  const toolSelected = pickBy(allTools, { name: tool });
  return toolSelected[tool].optional_environment_variables ? toolSelected[tool].optional_environment_variables.length > 0 : false;
};

export const selectToolHasBeenConfigured = (state, tool) => {
  const allTools = selectAgentTypes(state);
  const currentTool = pickBy(allTools, { name: tool });
  const enviromentVariables = get(currentTool, `${tool}.environment_variables`, []);
  const draftNewAgentTools = selectDraftNewAgentTools(state);
  const toolSelected = head(filter(draftNewAgentTools, { name: tool }));
  const isConfigured = [];
  if (toolSelected && !isEmpty(toolSelected.variables)) {
    enviromentVariables.forEach((item) => {
      const envVarConfigured = Object.hasOwn(toolSelected.variables, item) || !isEmpty(toolSelected.variables[item]);
      isConfigured.push(envVarConfigured);
    });
  }
  return !isEmpty(isConfigured) ? !isConfigured.includes(false) : false;
};

export const selectToolsVariables = (state, tool) => {
  const draftNewAgentTools = selectDraftNewAgentTools(state);
  const toolSelected = head(filter(draftNewAgentTools, { name: tool }));
  return get(toolSelected, 'variables', {});
};

export const selectToolError = (state, tool) => {
  const errors = get(state, 'agents.errors', {});
  return get(errors, tool, false);
};

export const selectAgentError = (state) => get(state, 'agents.error', false);

export const selectToken = (state) => get(state, 'agents.token', null);

export const selectTokenExpiration = (state) => get(state, 'agents.tokenExpiresIn', 0);

// Selector to get the list of all agents
export const selectAgentList = (state) => {
  // Check the condition using lodash get
  const showCloudAgents = get(state, 'agents.showCloudAgents', false);

  // Return cloudAgents or userAgents based on the condition
  return showCloudAgents
    ? get(state, 'agents.cloudAgents', [])
    : get(state, 'agents.userAgents', []);
};

// Selector to get the list of selected agents
export const selectAgentsSelected = (state) => get(state, 'agents.agentsSelected', []);

// Selector to get the last selected agent's index
export const selectLastSelected = (state) => get(state, 'agents.lastSelected', -1);

// Selector to get the total count of agents
export const selectAgentCount = (state) => get(state, 'agents.userAgentsCount', 0);

export const allAgentsAreSelected = (state) => {
  const agents = selectAgentList(state);
  const agentList = selectAgentsSelected(state);

  return agents.every((testAgent) => agentList.some((agent) => agent.id === testAgent.id)) && agentList.length > 0;
};

export const someAgentIsSelected = (state) => (selectAgentsSelected(state).length > 0);

export const SelectShowCloudAgents = (state) => get(state, 'agents.showCloudAgents', false);

export const selectCloudAgentCount = (state) => get(state, 'agents.cloudAgentsCount', 0);

export const selectIsFetchingAgent = (state) => get(state, 'agent.isFetchingAgent', false);

export const selectToolSelected = (state) => get(state, 'agent.triggerAgent.toolSelected', {});

export const selectAgentExecutions = (state) => get(state, 'agents.agentExecutions', []);

export const selectAgentExecutionSelected = (state) => get(state, 'agents.agentExecutionsSelected', []);

export const selectResultExecutions = (state) => get(state, 'agents.resultExecutions', []);

export const selectAgentExecutionsCount = (state) => get(state, 'agents.agentExecutionsCount', 0);

export const selectAgentSuccess = (state) => get(state, 'agents.success', '');

export const selectAgentEntity = (state) => get(state, 'agents.agentEntity', RUNNERS_ENTITY);
